/**
 * JSON-LD Schema Generators for Dr. Hani Mulla
 * Generates authoritative Physician + MedicalClinic structured data
 */

// Import the data directly (works in browser/CRA environment)
import doctorData from "../data/doctor-hani.json";

export type JSONLD = Record<string, any>;

export interface DoctorData {
  doctor: {
    name: string;
    name_en: string;
    given_name?: string;
    family_name?: string;
    given_name_he?: string;
    family_name_he?: string;
    license_number: string;
    specialist_license_number: string;
    medical_specialties: string[];
    medical_specialties_he?: string[];
    procedures_highlights?: string[];
    phone: string;
    fax?: string;
    email_primary: string;
    email_clinic?: string;
    same_as?: string[];
    education_training?: Array<{
      institution: string;
      institution_location?: string;
      country: string;
      degree_or_program?: string;
      program?: string;
      honors?: string;
    }>;
    affiliations_current?: Array<{
      organization: string;
      organization_he?: string;
      role: string;
      role_he?: string;
      city: string;
      city_he?: string;
      country: string;
      start_year?: number;
    }>;
    clinic_roles?: Array<{
      organization: string;
      organization_en?: string;
      role: string;
      role_he?: string;
      city: string;
      city_he?: string;
      country: string;
    }>;
    professional_memberships?: string[];
  };
  clinic: {
    name: string;
    name_en?: string;
    address: {
      streetAddress: string;
      addressLocality: string;
      addressLocality_en?: string;
      postalCode: string;
      addressCountry: string;
    };
    phone: string;
    fax?: string;
    email: string;
    url: string;
    openingHours?: string;
    geo?: {
      latitude: string;
      longitude: string;
    };
    images?: string[];
    available_languages?: string[];
  };
}

/**
 * Load doctor data (exported for flexibility, but defaults to imported data)
 */
export function loadDoctorData(): DoctorData {
  return doctorData as DoctorData;
}

/**
 * Generates Physician JSON-LD schema
 * Includes license identifiers, affiliations, credentials, and contact info
 */
export function physicianJSONLD(data: DoctorData): JSONLD {
  const d = data.doctor;
  const c = data.clinic;

  // Build address from clinic data
  const address = c?.address && {
    "@type": "PostalAddress",
    streetAddress: c.address.streetAddress,
    addressLocality: c.address.addressLocality,
    postalCode: c.address.postalCode,
    addressCountry: c.address.addressCountry,
  };

  // Build license identifiers
  const identifiers = [
    d.license_number && {
      "@type": "PropertyValue",
      propertyID: "MOH License",
      name: "Israel Ministry of Health License Number",
      value: d.license_number,
    },
    d.specialist_license_number && {
      "@type": "PropertyValue",
      propertyID: "Specialist License",
      name: "Specialist License Number",
      value: d.specialist_license_number,
    },
  ].filter(Boolean);

  // Build affiliations (hospitals where doctor practices)
  const affiliations = (d.affiliations_current || []).map((a) => ({
    "@type": "Organization",
    name: a.organization,
    address: {
      "@type": "PostalAddress",
      addressLocality: a.city,
      addressCountry: a.country,
    },
  }));

  // Build educational credentials
  const credentials = (d.education_training || []).map((e) => {
    const credentialName = [e.program, e.degree_or_program, e.honors]
      .filter(Boolean)
      .join(" - ");

    return {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "MedicalDegreeOrTraining",
      recognizedBy: {
        "@type": "Organization",
        name: e.institution,
        address: e.country && {
          "@type": "PostalAddress",
          addressCountry: e.country,
        },
      },
      educationalLevel: "Postgraduate",
      name: credentialName,
      awardingBody: e.institution,
    };
  });

  // Build member organizations (professional societies)
  const memberOf = (d.professional_memberships || []).map((org) => ({
    "@type": "Organization",
    name: org,
  }));

  // Build alternate names (Hebrew, Arabic, English, French, Russian)
  const alternateNames = [
    d.name, // Hebrew name
    (d as any).name_ar, // Arabic name
    d.name_en, // English name
    (d as any).name_fr, // French name
    (d as any).name_ru, // Russian name
  ].filter(Boolean);

  // Construct the Physician schema
  const schema: JSONLD = {
    "@context": "https://schema.org",
    "@type": "Physician",
    // Stable identifier so the doctor node can be referenced rather than
    // re-described. Must stay in step with the static copy in
    // public/index.html, which is the block actually served today.
    "@id": `${c.url.replace(/\/$/, "")}/#physician`,
    name: d.name_en || d.name,
    alternateName: alternateNames,
    // Split name fields, Hebrew first. `name` above is the English form, so a
    // consumer matching a Hebrew result must read the WHOLE array, not [0].
    // Both values are byte-slices of d.name / d.name_en, never retyped.
    givenName: [d.given_name_he, d.given_name].filter(Boolean),
    familyName: [d.family_name_he, d.family_name].filter(Boolean),
    medicalSpecialty: d.medical_specialties,
    description: d.procedures_highlights
      ? d.procedures_highlights.join(". ")
      : undefined,
    image: c.images?.[0],
    url: c.url,
    // Multilingual descriptions for broader search coverage
    ...(((d as any).procedures_highlights_en || (d as any).procedures_highlights_ar) && {
      "additionalType": "https://schema.org/Physician",
    }),
  };

  // Add optional fields only if they exist
  if (address) schema.address = address;
  if (d.email_primary) schema.email = d.email_primary;
  if (d.phone) schema.telephone = d.phone;
  if (d.fax) schema.faxNumber = d.fax;
  if (identifiers.length) schema.identifier = identifiers;
  if (affiliations.length) schema.affiliation = affiliations;
  if (credentials.length) schema.hasCredential = credentials;
  if (memberOf.length) schema.memberOf = memberOf;
  if (d.same_as?.length) schema.sameAs = d.same_as;
  if (c.available_languages?.length) {
    schema.availableLanguage = c.available_languages;
  }

  // Conditions the doctor treats (non-visible; helps Google + AI connect the
  // site to the symptom/condition terms patients actually search).
  schema.knowsAbout = [
    "פריצת דיסק",
    "היצרות תעלת השדרה",
    "סיאטיקה",
    "כאב גב תחתון",
    "כאב צוואר",
    "מיקרודיסקטומיה",
    "ניתוח עמוד שדרה זעיר פולשני",
    "צריבת דיסק בגלי רדיו",
    "Herniated disc",
    "Spinal stenosis",
    "Sciatica",
    "Minimally invasive spine surgery",
  ];

  // Service area (Galilee + the clinic cities).
  schema.areaServed = [
    { "@type": "AdministrativeArea", name: "הגליל" },
    { "@type": "City", name: "ירכא" },
    { "@type": "City", name: "חיפה" },
    { "@type": "City", name: "נהריה" },
  ];

  // Remove undefined values
  return JSON.parse(JSON.stringify(schema));
}

/**
 * Generates MedicalClinic JSON-LD schema
 * Includes address, contact info, opening hours, and geo coordinates
 */
export function clinicJSONLD(data: DoctorData): JSONLD {
  const c = data.clinic;

  // Alternate names for multilingual search discovery
  const clinicAlternateNames = [
    c.name_en,
    (c as any).name_ar,
    (c as any).name_fr,
    (c as any).name_ru,
  ].filter(Boolean);

  const schema: JSONLD = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "@id": `${c.url.replace(/\/$/, "")}/#clinic`,
    name: c.name,
    alternateName: clinicAlternateNames.length ? clinicAlternateNames : undefined,
    url: c.url,
    image: c.images?.[0] || c.images,
    address: {
      "@type": "PostalAddress",
      streetAddress: c.address.streetAddress,
      addressLocality: c.address.addressLocality,
      postalCode: c.address.postalCode,
      addressCountry: c.address.addressCountry,
    },
  };

  // Add optional fields
  if (c.phone) schema.telephone = c.phone;
  if (c.fax) schema.faxNumber = c.fax;
  if (c.email) schema.email = c.email;
  if (c.openingHours) schema.openingHours = c.openingHours;

  if (c.geo?.latitude && c.geo?.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: c.geo.latitude,
      longitude: c.geo.longitude,
    };
  }

  if (c.available_languages?.length) {
    schema.availableLanguage = c.available_languages;
  }

  // Add medical specialties from doctor data
  if (data.doctor.medical_specialties?.length) {
    schema.medicalSpecialty = data.doctor.medical_specialties;
  }

  // Remove undefined values
  return JSON.parse(JSON.stringify(schema));
}

/**
 * Generates BreadcrumbList JSON-LD schema
 * @param items Array of {name, url} breadcrumb items
 */
export function breadcrumbJSONLD(
  items: Array<{ name: string; url: string }>
): JSONLD {
  const baseUrl = "https://mulla-neurospine.co.il";
  
  const breadcrumbItems = items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url.startsWith("http") ? item.url : `${baseUrl}${item.url}`,
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems,
  };
}

/**
 * Convenience function to get all JSON-LD schemas at once
 */
export function getAllSchemas(): {
  physician: JSONLD;
  clinic: JSONLD;
} {
  const data = loadDoctorData();
  return {
    physician: physicianJSONLD(data),
    clinic: clinicJSONLD(data),
  };
}
